import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Settings, Navigation } from 'lucide-react';
import { STATUS_OPTIONS, CATEGORY_OPTIONS, SUB_CATEGORY_MAP, US_STATES, STATE_NAMES, SOURCE_OPTIONS, SENIORITY_OPTIONS } from './constants';
import { DuplicateWarning } from './DuplicateWarning';
import { AddressAutocomplete } from './AddressAutocomplete';
import api from '@/lib/api';

export function LeadFormDialog({ open, onClose, form, setForm, editLead, handleCreate, handleUpdate, customFields, setCF, duplicates, duplicateLoading, onCheckDuplicates, onDismissDuplicates, onCreateAnyway, onMergeDuplicate }) {
  const [catMap, setCatMap] = useState(null);

  useEffect(() => {
    if (open) {
      api.getCategories().then(r => setCatMap(r.data.categories)).catch(() => setCatMap(null));
    }
  }, [open]);

  const categoryOptions = catMap ? Object.keys(catMap).sort() : CATEGORY_OPTIONS;
  const subCategoryOptions = catMap ? (catMap[form.category] || []) : (SUB_CATEGORY_MAP[form.category] || []);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle></DialogHeader>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-2 mb-2 pb-1 border-b border-slate-200">Contact Details</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Full Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="lead-form-name" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Title</Label>
            <Input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. VP of Sales" className="mt-1 rounded-sm" data-testid="lead-form-title" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Department</Label>
            <Input value={form.department || ''} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="e.g. Marketing" className="mt-1 rounded-sm" data-testid="lead-form-department" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Seniority</Label>
            <Select value={form.seniority || "none"} onValueChange={v => setForm({ ...form, seniority: v === 'none' ? '' : v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="lead-form-seniority"><SelectValue placeholder="Select seniority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select seniority</SelectItem>
                {SENIORITY_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Primary Email</Label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="lead-form-email" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Alternate Email</Label>
            <Input type="email" value={form.alternate_email || ''} onChange={e => setForm({ ...form, alternate_email: e.target.value })} className="mt-1 rounded-sm" data-testid="lead-form-alt-email" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Primary Number</Label>
            <Input value={form.phone} onChange={e => {
              const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
              let formatted = raw;
              if (raw.length > 6) formatted = `(${raw.slice(0,3)}) ${raw.slice(3,6)}-${raw.slice(6)}`;
              else if (raw.length > 3) formatted = `(${raw.slice(0,3)}) ${raw.slice(3)}`;
              else if (raw.length > 0) formatted = `(${raw}`;
              setForm({ ...form, phone: formatted });
            }} placeholder="(555) 123-4567" className="mt-1 rounded-sm" data-testid="lead-form-phone" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Mobile Number</Label>
            <Input value={form.mobile_number || ''} onChange={e => {
              const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
              let formatted = raw;
              if (raw.length > 6) formatted = `(${raw.slice(0,3)}) ${raw.slice(3,6)}-${raw.slice(6)}`;
              else if (raw.length > 3) formatted = `(${raw.slice(0,3)}) ${raw.slice(3)}`;
              else if (raw.length > 0) formatted = `(${raw}`;
              setForm({ ...form, mobile_number: formatted });
            }} placeholder="(555) 123-4567" className="mt-1 rounded-sm" data-testid="lead-form-mobile" />
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-4 mb-2 pb-1 border-b border-slate-200">Company</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Company Name</Label>
            <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="mt-1 rounded-sm" data-testid="lead-form-company" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Website</Label>
            <Input type="url" value={form.website || ''} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://example.com" className="mt-1 rounded-sm" data-testid="lead-form-website" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Size (Employees)</Label>
            <Input type="number" min="0" value={form.number_of_employees ?? ''} onChange={e => setForm({ ...form, number_of_employees: e.target.value === '' ? null : parseInt(e.target.value, 10) })} placeholder="e.g. 50" className="mt-1 rounded-sm" data-testid="lead-form-employees" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Revenue</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <Input type="number" min="0" step="0.01" value={form.annual_revenue ?? ''} onChange={e => setForm({ ...form, annual_revenue: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="e.g. 1000000" className="rounded-sm pl-7" data-testid="lead-form-revenue" />
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Industry</Label>
            <Input value={form.industry || ''} onChange={e => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Professional Services" className="mt-1 rounded-sm" data-testid="lead-form-industry" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">SIC Code</Label>
            <Input value={form.sic_code || ''} onChange={e => setForm({ ...form, sic_code: e.target.value })} placeholder="e.g. 8721" className="mt-1 rounded-sm" data-testid="lead-form-sic" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">NAICS Code</Label>
            <Input value={form.naics_code || ''} onChange={e => setForm({ ...form, naics_code: e.target.value })} placeholder="e.g. 541219" className="mt-1 rounded-sm" data-testid="lead-form-naics" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Business Start Date</Label>
            <Input type="date" value={form.business_start_date || ''} onChange={e => setForm({ ...form, business_start_date: e.target.value })} className="mt-1 rounded-sm" data-testid="lead-form-business-start-date" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Years in Business</Label>
            <Input
              type="text"
              readOnly
              value={form.business_start_date ? (() => { const y = (Date.now() - new Date(form.business_start_date).getTime()) / (365.25 * 24 * 60 * 60 * 1000); return y < 1 ? '<1' : Math.floor(y).toString(); })() : ''}
              placeholder="Auto-calculated"
              className="mt-1 rounded-sm bg-slate-50 cursor-default text-slate-600"
              tabIndex={-1}
              data-testid="lead-form-years-in-business"
            />
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-4 mb-2 pb-1 border-b border-slate-200">Address</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Street Address</Label>
            <AddressAutocomplete
              value={form.address}
              onChange={v => setForm({ ...form, address: v })}
              onPlaceSelect={(parsed) => {
                // Only overwrite the state field if Google returned a US 2-letter
                // code that exists in our US_STATES whitelist; otherwise leave
                // the user's current selection alone (e.g. for international leads).
                const next = { ...form, address: parsed.address, city: parsed.city || form.city, zip_code: parsed.zip_code || form.zip_code };
                if (parsed.state && US_STATES.includes(parsed.state)) next.state = parsed.state;
                setForm(next);
              }}
              placeholder="Start typing — Google will suggest matches"
              testid="lead-form-address"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">City</Label>
            <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="mt-1 rounded-sm" data-testid="lead-form-city" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">State</Label>
            <Select value={form.state || "none"} onValueChange={v => setForm({ ...form, state: v === 'none' ? '' : v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="lead-form-state"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-48"><SelectItem value="none">Select state</SelectItem>{US_STATES.map(s => <SelectItem key={s} value={s}>{s} — {STATE_NAMES[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Zip</Label>
            <Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} className="mt-1 rounded-sm" data-testid="lead-form-zip" />
          </div>
          {(form.address || form.city || form.state) && (() => {
            const fullAddress = [form.address, form.city, form.state, form.zip_code].filter(Boolean).join(', ');
            const encoded = encodeURIComponent(fullAddress);
            return (
              <div className="col-span-2 space-y-2" data-testid="lead-form-map-preview">
                <div className="rounded-sm overflow-hidden border border-slate-200">
                  <iframe
                    title="Address Preview"
                    width="100%"
                    height="180"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encoded}&t=k&z=17&ie=UTF8&iwloc=&output=embed`}
                  />
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encoded}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#0047AB] hover:bg-[#003580] text-white text-xs font-medium transition-colors"
                  data-testid="lead-form-get-directions"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  Get directions
                </a>
              </div>
            );
          })()}
        </div>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-4 mb-2 pb-1 border-b border-slate-200">Pipeline</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="lead-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Lead Source</Label>
            <Select value={form.lead_source || "none"} onValueChange={v => setForm({ ...form, lead_source: v === 'none' ? '' : v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="lead-form-source"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select source</SelectItem>
                {SOURCE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Category</Label>
            <Select value={form.category || "none"} onValueChange={v => { setForm({ ...form, category: v === 'none' ? '' : v, sub_category: '' }); }}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="lead-form-category"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Sub-Category</Label>
            <Select value={form.sub_category || "none"} onValueChange={v => setForm({ ...form, sub_category: v === 'none' ? '' : v })} disabled={!form.category}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="lead-form-sub-category"><SelectValue placeholder={form.category ? 'Select sub-category' : 'Select category first'} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select sub-category</SelectItem>
                {subCategoryOptions.map(sc => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Tags (comma separated)</Label>
            <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="hot, enterprise" className="mt-1 rounded-sm" data-testid="lead-form-tags" />
          </div>
          {customFields.length > 0 && (
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-medium mb-2">Custom Fields</p>
              <div className="grid grid-cols-2 gap-3">
                {customFields.map(cf => (
                  <div key={cf.id}>
                    <Label className="text-xs font-medium text-slate-700">{cf.name}{cf.required && ' *'}</Label>
                    {cf.field_type === 'text' && <Input value={form.custom_fields?.[cf.field_key] || ''} onChange={e => setCF(cf.field_key, e.target.value)} className="mt-1 rounded-sm text-sm" data-testid={`cf-${cf.field_key}`} />}
                    {cf.field_type === 'number' && <Input type="number" value={form.custom_fields?.[cf.field_key] || ''} onChange={e => setCF(cf.field_key, e.target.value)} className="mt-1 rounded-sm text-sm" data-testid={`cf-${cf.field_key}`} />}
                    {cf.field_type === 'date' && <Input type="date" value={form.custom_fields?.[cf.field_key] || ''} onChange={e => setCF(cf.field_key, e.target.value)} className="mt-1 rounded-sm text-sm" data-testid={`cf-${cf.field_key}`} />}
                    {cf.field_type === 'checkbox' && (
                      <label className="flex items-center gap-2 mt-1.5"><input type="checkbox" checked={!!form.custom_fields?.[cf.field_key]} onChange={e => setCF(cf.field_key, e.target.checked)} className="rounded-sm" data-testid={`cf-${cf.field_key}`} /><span className="text-xs text-slate-600">Yes</span></label>
                    )}
                    {cf.field_type === 'select' && (
                      <Select value={form.custom_fields?.[cf.field_key] || "none"} onValueChange={v => setCF(cf.field_key, v === 'none' ? '' : v)}>
                        <SelectTrigger className="mt-1 rounded-sm text-sm" data-testid={`cf-${cf.field_key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Select</SelectItem>{cf.options?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!editLead && (
            <div className="col-span-2">
              <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Initial Note</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" rows={2} data-testid="lead-form-notes" />
            </div>
          )}
        </div>
        {/* Duplicate Warning */}
        {!editLead && duplicates && duplicates.length > 0 && (
          <DuplicateWarning
            duplicates={duplicates}
            onDismiss={onDismissDuplicates}
            onProceed={onCreateAnyway}
            onMerge={onMergeDuplicate}
            loading={duplicateLoading}
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="lead-form-cancel">Cancel</Button>
          {editLead ? (
            <Button onClick={handleUpdate} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="lead-form-submit">Update Lead</Button>
          ) : (
            <Button onClick={onCheckDuplicates || handleCreate} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" disabled={duplicateLoading} data-testid="lead-form-submit">
              {duplicateLoading ? 'Checking...' : 'Create Lead'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FieldManagerDialog({ open, onClose, customFields, newField, setNewField, handleAddField, handleDeleteField }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-sm" data-testid="field-manager-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Settings className="w-4 h-4" /> Manage Custom Fields</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {customFields.length > 0 ? customFields.map(cf => (
            <div key={cf.id} className="flex items-center justify-between p-2.5 border border-slate-200 rounded-sm" data-testid={`field-${cf.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-900">{cf.name}</p>
                <p className="text-[10px] text-slate-400">{cf.field_type}{cf.required ? ' (required)' : ''}{cf.options?.length ? ` — ${cf.options.join(', ')}` : ''}</p>
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteField(cf.id)} data-testid={`delete-field-${cf.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          )) : <p className="text-sm text-slate-400 text-center py-4">No custom fields yet</p>}
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs font-medium text-slate-700 mb-2">Add New Field</p>
            <div className="grid grid-cols-2 gap-2">
              <Input value={newField.name} onChange={e => setNewField({ ...newField, name: e.target.value })} placeholder="Field name" className="rounded-sm text-sm" data-testid="new-field-name" />
              <Select value={newField.field_type} onValueChange={v => setNewField({ ...newField, field_type: v })}>
                <SelectTrigger className="rounded-sm text-sm" data-testid="new-field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem><SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem><SelectItem value="select">Dropdown</SelectItem>
                  <SelectItem value="checkbox">Checkbox</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newField.field_type === 'select' && (
              <Input value={newField.options} onChange={e => setNewField({ ...newField, options: e.target.value })} placeholder="Options (comma separated)" className="mt-2 rounded-sm text-sm" data-testid="new-field-options" />
            )}
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={newField.required} onChange={e => setNewField({ ...newField, required: e.target.checked })} className="rounded-sm" /> Required</label>
              <Button onClick={handleAddField} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs h-7 px-3" data-testid="add-field-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
